import { SyntheticEvent, useCallback, useState } from "react"

import { ApiError, ErrorsMap } from "api/http/utils"
import ConfirmationModal, {
  ModalInfo,
} from "components/Modals/ConfirmationModal"
import {
  activateCreativeAgency,
  createCreativeAgency,
  deactivateCreativeAgency,
  deleteCreativeAgency,
  fetchCreativeAgencies,
  patchCreativeAgency,
} from "api/creativeAgencies"
import { queryCache, useMutation, useQuery } from "react-query"

import { COLUMNS } from "./columns"
import { ReactComponent as CheckIcon } from "assets/check.svg"
import { Col } from "components/Rable/types"
import { CreativeAgency } from "models/creativeAgencies"
import { ReactComponent as Deactivate } from "assets/icon-deactivate.svg"
import { ReactComponent as DeleteIcon } from "assets/delete.svg"
import { Rable } from "components/Rable"
import { SnackBar } from "components/SnackBar"
import { css } from "@emotion/css"
import { useAuthenticated } from "providers/A12n"
import { useI18n } from "providers/I18n"
import { useSendFeedBack } from "components/FeedbackSnackbar"

const CreativeAgencies = () => {
  const { t } = useI18n()
  const { hasAuthority } = useAuthenticated()
  const [modalInfo, setModalInfo] = useState<ModalInfo>()
  const feedback = useSendFeedBack()
  const [openCreate, setOpenCreate] = useState<boolean>(false)
  const isModalOpen = Boolean(modalInfo)
  const [error, setError] = useState<Error>()
  const [tableError, setTableError] = useState<ErrorsMap[]>([])

  const { data: creativeAgencies, isLoading } = useQuery(
    ["creative-agencies"],
    fetchCreativeAgencies,
  )

  const [create] = useMutation(createCreativeAgency, {
    onSuccess: () => {
      setOpenCreate(false)
      queryCache.invalidateQueries("creative-agencies")
    },
    onError: (err) => {
      feedback(t(err.message), "error")
    },
  })

  const onCreate = (newRow: Partial<CreativeAgency>) => {
    const { name, code, rut, ...rest } = newRow
    const _newRow = Object?.keys(rest).reduce((acc, curr) => {
      const _key = curr?.split(".")
      return { ...acc, [_key[1]]: newRow[curr as keyof typeof newRow] }
    }, {})
    return create({
      name: newRow.name,
      code: newRow.code,
      rut: newRow.rut,
      ..._newRow,
    })
  }

  const [remove] = useMutation(deleteCreativeAgency, {
    onSuccess: () => {
      setModalInfo(undefined)
      queryCache.invalidateQueries("creative-agencies")
    },
    onError: (err) => {
      setModalInfo(undefined)
      feedback(t(err.message), "error")
    },
  })

  const handleDelete = useCallback(
    (row: CreativeAgency) => {
      setModalInfo({
        title: t("market.creativeAgencies.modal.delete.title"),
        type: "primary_destructive",
        description: t("market.creativeAgencies.modal.delete.description"),
        submitLabel: t("modal.label.delete"),
        onSubmit: async () => await remove({ creativeAgencyId: row.id || "" }),
      })
    },
    [remove, t],
  )

  const [patch] = useMutation(patchCreativeAgency, {
    onSuccess: () => {
      queryCache.invalidateQueries("creative-agencies")
    },
    onError: (err: ApiError, variables: Partial<CreativeAgency>) => {
      const errorMap = err?.fields || []

      setTableError((s) => [
        ...s,
        ...errorMap?.map((e: any) => ({ ...e, id: variables.id })),
      ])
    },
  })

  const onUpdate = useCallback(
    (rowId: string, row: Partial<CreativeAgency>) => {
      patch({
        id: rowId,
        ...row,
      })
    },
    [patch],
  )

  const [activate] = useMutation(activateCreativeAgency, {
    onSuccess: () => {
      setModalInfo(undefined)
      queryCache.invalidateQueries("creative-agencies")
    },
    onError: (err) => {
      setModalInfo(undefined)
      feedback(t(err.message), "error")
    },
  })

  const handleActivate = useCallback(
    (row: CreativeAgency) => {
      setModalInfo({
        title: t("admin.creativeAgencies.activate.title"),
        description: t("admin.creativeAgencies.activate.description"),
        submitLabel: t("modal.label.activate"),
        cancelLabel: t("modal.label.cancel"),
        onSubmit: async () =>
          await activate({ creativeAgencyId: row.id || "" }),
      })
    },
    [activate, t],
  )

  const [deactivate] = useMutation(deactivateCreativeAgency, {
    onSuccess: () => {
      setModalInfo(undefined)
      queryCache.invalidateQueries("creative-agencies")
    },
    onError: (err) => {
      setModalInfo(undefined)
      feedback(t(err.message), "error")
    },
  })

  const handleDeactivate = useCallback(
    (row: CreativeAgency) => {
      setModalInfo({
        type: "primary_destructive",
        title: t("admin.creativeAgencies.deactivate.title"),
        description: t("admin.creativeAgencies.deactivate.description"),
        submitLabel: t("modal.label.deactivate"),
        cancelLabel: t("modal.label.cancel"),
        onSubmit: async () =>
          await deactivate({ creativeAgencyId: row.id || "" }),
      })
    },
    [deactivate, t],
  )

  const handleCloseError = (event?: SyntheticEvent, reason?: string) => {
    if (reason === "clickaway") {
      return
    }
    setError(undefined)
  }

  const onOpenCreate = () => setOpenCreate(true)
  const onCloseCreate = () => setOpenCreate(false)

  const creativeAgenciesMapped = creativeAgencies?.map((agency) => {
    return agency
  })

  const required = COLUMNS?.filter((c) => c.required) as Col<
    Partial<CreativeAgency>
  >[]

  const newRowCols = COLUMNS.filter((c) => c.existsInCreate) as Col<
    Partial<CreativeAgency>
  >[]

  const getTableError = (id: string, name: string) => {
    if (
      !!tableError?.length &&
      tableError.some((e) => {
        return e.id === id && e.name === name
      })
    )
      return name
    else return undefined
  }

  const clearErrors = (id: string, name: string) => {
    const idx = tableError?.findIndex((e) => e?.id === id && e?.name === name)
    let err = [...tableError]
    if (idx > -1) {
      err?.splice(idx, 1)
    }
    setTableError(err)
  }

  const actions = (row: CreativeAgency) => [
    //ACTIVATE
    {
      label: t("activate"),
      icon: <CheckIcon />,
      hidden:
        row.status === "ACTIVE" ||
        row.name === "" ||
        row.rut === "" ||
        row.code === "" ||
        tableError.some((e) => e.id === row.id),
      onClick: () => handleActivate(row),
    },
    //DEACTIVATE
    {
      label: t("deactivate"),
      icon: <Deactivate />,
      hidden: row.status !== "ACTIVE",
      onClick: () => handleDeactivate(row),
    },
    // DELETE
    {
      label: t("actions.delete"),
      icon: <DeleteIcon />,
      hidden: row.status !== "DRAFT",
      onClick: () => handleDelete(row),
    },
  ]

  return (
    <div className={styles.wrapper}>
      <Rable<CreativeAgency>
        getError={getTableError}
        clearError={clearErrors}
        data={creativeAgenciesMapped || []}
        columns={COLUMNS}
        required={required}
        createRow={onCreate}
        newRowColumns={newRowCols}
        updateRow={onUpdate}
        onOpen={
          hasAuthority(["AUTH_CREATE_CREATIVE_AGENCY"])
            ? onOpenCreate
            : undefined
        }
        btnClassName={styles.button}
        tableTitle={t("admin.creativeAgencies.title")}
        createBtnVariant="default"
        label="rate.card.loading"
        open={openCreate}
        onClose={onCloseCreate}
        pending={isLoading}
        hasActions={hasAuthority(["AUTH_ACCESS_MARKET_FINANCE"])}
        actions={actions}
        createButtonLabel={t("market.creativeAgencies.add.row")}
        hasToolbar={true}
        emptyState={
          <>
            <div className={styles.emptyMessage}>
              {t("market.creativeAgencies.empty.table")}
            </div>
          </>
        }
      />
      <SnackBar
        open={!!error}
        severity="error"
        onClose={handleCloseError}
        message={error?.message || ""}
      />
      <ConfirmationModal
        open={isModalOpen}
        onCancel={() => setModalInfo(undefined)}
        {...modalInfo}
      />
    </div>
  )
}
export default CreativeAgencies

const styles = {
  wrapper: css`
    margin-top: 32px;
    > div {
      padding: 0;
    }
  `,
  table: css`
    .RebaseTable__cell.RebaseTable__grid-cell {
      position: relative;
    }
  `,
  button: css`
    --btn-padding: 0;
    --btn-min-width: 0;
    --btn-line-height: 1;
    --btn-color: inherit;
    --btn-font-weight: normal;
    --btn-bg-hover: transparent;
    --btn-bg-active: transparent;
    --btn-border-color-hover: transparent;
    --btn-bg-focus-ring-color: transparent;
    width: fit-content;
    &:focus {
      box-shadow: none;
    }
  `,
  emptyMessage: css`
    display: flex;
    margin: 0;
    margin-bottom: 8px;
    padding-top: var(--spacing-xxl-2);
    font-size: 16px;
    font-weight: 300;
    text-align: center;
    color: var(--dark-grey);
  `,
}
